import React, { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, useNavigationType } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider } from "./lib/auth";
import { CartProvider } from "./lib/cart";
import { ThemeProvider } from "./lib/theme";
import { useAnalyticsPageviews } from "./lib/analytics";
import RouteErrorBoundary from "./components/RouteErrorBoundary";

import Home from "./pages/Home";
import Shop from "./pages/Shop";
import Brands from "./pages/Brands";
import ProductDetail from "./pages/ProductDetail";
import Cart from "./pages/Cart";
import CheckoutResult from "./pages/CheckoutResult";
import SEOLanding from "./pages/SEOLanding";

import AdminLogin from "./pages/admin/Login";
import ChangePassword from "./pages/admin/ChangePassword";
import AdminUsers from "./pages/admin/Users";
import AdminDashboard from "./pages/admin/Dashboard";
import AdminProducts from "./pages/admin/Products";
import AdminProductForm from "./pages/admin/ProductForm";
import AdminCoupons from "./pages/admin/Coupons";
import AdminInquiries from "./pages/admin/Inquiries";
import Contact from "./pages/Contact";
import About from "./pages/About";
import AdminVehicles from "./pages/admin/Vehicles";
import AdminOrders from "./pages/admin/Orders";
import AdminShipping from "./pages/admin/Shipping";
import AdminMerchantFeed from "./pages/admin/MerchantFeed";
import AdminBulkUpload from "./pages/admin/BulkUpload";
import FindMyVehicle from "./pages/FindMyVehicle";
import EVGlass from "./pages/EVGlass";
import Resources from "./pages/Resources";
import ResourceArticle from "./pages/ResourceArticle";
import Faq from "./pages/Faq";
import PaymentOptions from "./pages/PaymentOptions";
import ShippingPolicy from "./pages/policies/ShippingPolicy";
import ReturnPolicy from "./pages/policies/ReturnPolicy";
import RefundPolicy from "./pages/policies/RefundPolicy";
import WarrantyPolicy from "./pages/policies/WarrantyPolicy";
import PrivacyPolicy from "./pages/policies/PrivacyPolicy";
import TermsConditions from "./pages/policies/TermsConditions";


function AnalyticsTracker() {
  useAnalyticsPageviews();
  return null;
}

// Global scroll restoration: new routes open at the top; intentional
// hash anchors are honored; browser back/forward (POP) is left to the browser.
function ScrollToTop() {
  const { pathname, hash } = useLocation();
  const navType = useNavigationType();
  useEffect(() => {
    if (hash) {
      const el = document.getElementById(hash.slice(1));
      if (el) {
        el.scrollIntoView();
        return;
      }
    }
    if (navType !== "POP") {
      window.scrollTo(0, 0);
    }
  }, [pathname, hash, navType]);
  return null;
}

function App() {
  return (
    <div className="App">
      <ThemeProvider>
        <AuthProvider>
          <CartProvider>
            <BrowserRouter>
              <AnalyticsTracker />
              <ScrollToTop />
              <RouteErrorBoundary>
              <Routes>
              {/* Storefront */}
              <Route path="/" element={<Home />} />
              <Route path="/shop" element={<Shop />} />
              <Route path="/shop/:category" element={<Shop />} />
              <Route path="/brands" element={<Brands />} />
              <Route path="/vehicle/:vehicleId" element={<Shop />} />
              <Route path="/product/:slug" element={<ProductDetail />} />
              <Route path="/cart" element={<Cart />} />
              <Route path="/checkout/success" element={<CheckoutResult />} />
              <Route path="/checkout/cancel" element={<Cart />} />
              <Route path="/find-my-vehicle" element={<FindMyVehicle />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/about" element={<About />} />
              <Route path="/ev-glass" element={<EVGlass />} />
              <Route path="/resources" element={<Resources />} />
              <Route path="/resources/:slug" element={<ResourceArticle />} />
              <Route path="/faq" element={<Faq />} />
              <Route path="/payment-options" element={<PaymentOptions />} />

              {/* Policy / legal pages (must be before the catch-all SEO routes) */}
              <Route path="/shipping-policy" element={<ShippingPolicy />} />
              <Route path="/return-policy" element={<ReturnPolicy />} />
              <Route path="/refund-policy" element={<RefundPolicy />} />
              <Route path="/warranty-policy" element={<WarrantyPolicy />} />
              <Route path="/privacy-policy" element={<PrivacyPolicy />} />
              <Route path="/terms-conditions" element={<TermsConditions />} />

              {/* Admin */}
              <Route path="/admin/login" element={<AdminLogin />} />
              <Route path="/admin/change-password" element={<ChangePassword />} />
              <Route path="/admin" element={<AdminDashboard />} />
              <Route path="/admin/products" element={<AdminProducts />} />
              <Route path="/admin/products/new" element={<AdminProductForm />} />
              <Route path="/admin/products/:id" element={<AdminProductForm />} />
              <Route path="/admin/coupons" element={<AdminCoupons />} />
              <Route path="/admin/inquiries" element={<AdminInquiries />} />
              <Route path="/admin/vehicles" element={<AdminVehicles />} />
              <Route path="/admin/orders" element={<AdminOrders />} />
              <Route path="/admin/shipping" element={<AdminShipping />} />
              <Route path="/admin/merchant-feed" element={<AdminMerchantFeed />} />
              <Route path="/admin/bulk-upload" element={<AdminBulkUpload />} />
              <Route path="/admin/users" element={<AdminUsers />} />

              {/* SEO landing — last so it doesn't shadow specific routes */}
              <Route path="/:p1" element={<SEOLanding />} />
              <Route path="/:p1/:p2" element={<SEOLanding />} />
              <Route path="/:p1/:p2/:p3" element={<SEOLanding />} />
            </Routes>
            </RouteErrorBoundary>
            <Toaster theme="dark" position="top-right" />
          </BrowserRouter>
        </CartProvider>
      </AuthProvider>
      </ThemeProvider>
    </div>
  );
}

export default App;

